import React, { useState, useCallback, useMemo, memo } from 'react';
import { MemeTemplate } from '@/types/aiAgents';
import { Download, Maximize2 } from 'lucide-react';

// Meme Template Grid Component
interface MemeTemplateGridProps {
  templates: MemeTemplate[];
  onTemplateSelect?: (template: MemeTemplate) => void;
  isDarkMode?: boolean;
}

export const MemeTemplateGrid: React.FC<MemeTemplateGridProps> = memo(({
  templates,
  onTemplateSelect,
  isDarkMode = false
}) => {
  const handleTemplateSelect = useCallback((template: MemeTemplate) => {
    onTemplateSelect?.(template);
  }, [onTemplateSelect]);

  const templateItems = useMemo(() => templates.map((template, index) => (
    <div
      key={`${template.id}-${index}`}
      onClick={() => handleTemplateSelect(template)}
      className={`rounded-2xl p-1 text-center backdrop-blur-sm border cursor-pointer 
        transition-all duration-200 hover:scale-101 hover:shadow-lg border-1 
        ${isDarkMode
          ? 'bg-[#252525]/90 border-[#232323]'
          : 'bg-gray-100/20 hover:bg-gray-200/30 border-gray-300'
        }`}
    >
      <img
        src={template.url}
        alt={template.name}
        className="w-full h-24 object-cover rounded-xl mb-2"
        loading="lazy"
      />
      <p className="text-xs font-semibold truncate leading-tight mb-1">
        {index + 1}. {template.name}
      </p>
    </div>
  )), [templates, handleTemplateSelect, isDarkMode]);

  return (
    <div className={`rounded-2xl py-2 sm:px-4 px-0 my-2 bg-transparent border-0`}>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {templateItems}
      </div>

      <div className={`pt-4 mt-1 px-2 rounded-xl ${isDarkMode ? 'bg-gray-700/20' : 'bg-gray-100/20'
        }`}>
        <h3 className="text-base font-semibold mb-2 flex items-center gap-1">
          💡 How to use:
        </h3>
        <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          <li>• Type the meme number or name (e.g., "1" for Drake Pointing or "distracted boyfriend")</li>
          <li>• Describe what you want (e.g., "make a meme about coding")</li>
        </ul>
      </div>
    </div>
  );
});

// Meme Result Card Component
interface MemeResultCardProps {
  memeUrl: string;
  templateName: string;
  reasoning?: string;
  isDarkMode?: boolean;
  onFullscreen?: (imageUrl: string) => void;
}

export const MemeResultCard: React.FC<MemeResultCardProps> = memo(({
  memeUrl,
  templateName,
  reasoning,
  isDarkMode = false,
  onFullscreen
}) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(memeUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `meme-${templateName.toLowerCase().replace(/\s+/g, '-')}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading meme:', error);
    }
  }, [memeUrl, templateName]);

  const handleFullscreen = useCallback(() => {
    // Dispatch event to open in the global fullscreen image modal (like pixabay/wiki)
    const event = new CustomEvent("openFullscreenImageModal", {
      detail: {
        images: [{ url: memeUrl, description: '' }], // No description - only image
        initialIndex: 0
      },
    });
    window.dispatchEvent(event);
    onFullscreen?.(memeUrl);
  }, [onFullscreen, memeUrl]);

  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  return (
    <div className={`rounded-2xl overflow-hidden backdrop-blur-sm border ${isDarkMode
      }`}>

      <div className={`rounded-2xl rounded-b-none p-3 backdrop-blur-sm ${isDarkMode
        ? 'bg-[#252525]/90 border-[#232323]'
        : 'bg-gray-100/20 hover:bg-gray-200/30 border-gray-300'
        }`}>
        <h3 className="mb-2 text-base font-semibold">{templateName}</h3>
        <div className="relative group max-w-sm mx-auto bg-transparent">
          <img
            src={memeUrl}
            alt="Generated Meme"
            className="w-full max-w-xs h-auto rounded-lg shadow-lg cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
            onLoad={handleImageLoad}
            onClick={handleFullscreen}
          />

          {/* Overlay buttons */}
          <div className="absolute inset-0 bg-transparent transition-colors duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFullscreen();
                }}
                className="bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200"
                title="View Fullscreen"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                className="bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200"
                title="Download Meme"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`text-xs pl-3 pb-3 ${isDarkMode
        ? 'bg-[#252525]/90 text-gray-200'
        : 'bg-gray-100/20 text-gray-600'
        }`}>
        <p>Want another meme? Just describe what you want!</p>
      </div>
    </div>
  );
});

// Meme Error Card Component
interface MemeErrorCardProps {
  error: string;
  isDarkMode?: boolean;
}

export const MemeErrorCard: React.FC<MemeErrorCardProps> = memo(({
  error,
  isDarkMode = false
}) => {
  return (
    <div className={`rounded-2xl p-6 my-4 backdrop-blur-sm border ${isDarkMode
        ? 'bg-red-900/10 border-red-700/20'
        : 'bg-red-50/30 border-red-200/30'
      }`}>
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-red-600">
        ❌ Meme Generation Failed
      </h2>
      <p className={`mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
        {error}
      </p>
      <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
        Please try again with a different request.
      </p>
    </div>
  );
});

// Main Meme Renderer Component
interface MemeRendererProps {
  content: string;
  isDarkMode?: boolean;
}

export const MemeRenderer: React.FC<MemeRendererProps> = ({
  content,
  isDarkMode = false
}) => {
  // Try to parse the content as JSON to see if it's meme component data
  let memeData;
  try {
    memeData = JSON.parse(content);
  } catch {
    // If it's not JSON, return null to let the regular renderer handle it
    return null;
  }

  // Check if this is meme component data
  if (!memeData || !memeData.type) {
    return null;
  }

  switch (memeData.type) {
    case 'meme-templates':
      return (
        <MemeTemplateGrid
          templates={memeData.templates}
          isDarkMode={isDarkMode}
        />
      );

    case 'meme-result':
      return (
        <MemeResultCard
          memeUrl={memeData.data.memeUrl}
          templateName={memeData.data.templateName}
          reasoning={memeData.data.reasoning}
          isDarkMode={isDarkMode}
        />
      );

    case 'meme-error':
      return (
        <MemeErrorCard
          error={memeData.data.error}
          isDarkMode={isDarkMode}
        />
      );

    default:
      return null;
  }
};
